import type { Building, ConsumptionGranularity, Meter, MeterConsumptionQuery } from "@eisox/api/core-api";

import { meterApi } from "~/api";
import { toInstantRange, type DayRange } from "~/helpers/day-range.helper";

import { BASELOAD_GRANULARITIES } from "./consumption.constant";
import type { ChartView, ConsumptionPeriod } from "./types/consumption.type";

type MeteredBuilding = Pick<Building, "id" | "timezone">;

const consumptionParams = (
  timezone: string,
  granularity: ConsumptionGranularity,
  range: DayRange,
  options: Omit<MeterConsumptionQuery, "granularity" | "startDate" | "endDate"> = {},
): MeterConsumptionQuery => ({ granularity, ...toInstantRange(range, timezone), ...options });

const indicatorParams = (granularity: ConsumptionGranularity): Pick<MeterConsumptionQuery, "average" | "baseload"> => ({
  average: true,
  baseload: BASELOAD_GRANULARITIES.includes(granularity),
});

const chartQueries = (
  { id, timezone }: MeteredBuilding,
  meterId: string,
  { period, degreeDays, comparison }: ChartView,
) => ({
  current: meterApi.queries.consumption(
    id,
    meterId,
    consumptionParams(timezone, period.granularity, period.range, {
      degreeDays,
      ...indicatorParams(period.granularity),
    }),
  ),
  compared:
    comparison &&
    meterApi.queries.consumption(
      id,
      meterId,
      consumptionParams(timezone, period.granularity, comparison.range, {
        degreeDays,
        objective: comparison.objective,
      }),
    ),
});

const hierarchyQueries = (
  { id, timezone }: MeteredBuilding,
  meterId: Meter["id"],
  subMeterIds: Meter["id"][],
  period: ConsumptionPeriod,
) => {
  const queryOf = (queriedId: Meter["id"]) =>
    meterApi.queries.consumption(id, queriedId, consumptionParams(timezone, period.granularity, period.range));
  return { meter: queryOf(meterId), subMeters: subMeterIds.map(queryOf) };
};

export { chartQueries, consumptionParams, hierarchyQueries, indicatorParams };
